import { Outlet, useLocation } from "@tanstack/react-router";
import { Suspense, useCallback, useEffect, useState } from "react";
import cn from "mcn";
import { useSetAtom } from "jotai";

import { webappTheme } from "../../styles/extendTheme";
import { ToasterContainer } from "../components/Toaster";
import { ModalProvider, ModalDragConstraintProvider } from "../components/Modal";
import { AppThemeProvider } from "../../context/theme";
import { LicenceAgreement } from "../licensing";
import { SessionProvider } from "../../context/sessions";
import { MfaOfferGate } from "../mfa/MfaOfferGate";
import { DialogProvider } from "../dialog";
import { UpdatePromptProvider, UpdateToastGate } from "../version-update";
import { PermissionChangeWatcher } from "../alerts/PermissionChangeWatcher";
import { Sidebar } from "./Sidebar";
import { MobileNavActionsProvider, MobileNavBar } from "./MobileNavBar";
import { useIsOpenInPopup } from "../../utils/react";
import { CollapsibleSettings } from "../grid-settings";
import { gridLoadingAtom } from "../../stateAtoms";

export { Sidebar } from "./Sidebar";

function LayoutContent() {
  const isOpenInPopup = useIsOpenInPopup();
  const location = useLocation();
  const setGridLoading = useSetAtom(gridLoadingAtom);

  const isCurveManagementPage = location.pathname.startsWith("/app/curve-management");
  const isCataloguePage = location.pathname === "/app/catalogue";
  const isLiveChartsPage = location.pathname.startsWith("/app/trading-chart");
  const isComponentsPage = location.pathname.endsWith("/components");
  const hideSidebar = isOpenInPopup || isCurveManagementPage || isCataloguePage || isLiveChartsPage;
  const showMobileNav = !isOpenInPopup && !isComponentsPage;

  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);
  const [customActions, setCustomActions] = useState(0);
  const setActionsPresent = useCallback((present: boolean) => {
    setCustomActions((count) => Math.max(0, count + (present ? 1 : -1)));
  }, []);

  useEffect(() => {
    const isMarketGridPage = location.pathname.startsWith("/app/market");
    if (!isMarketGridPage) {
      setGridLoading(false);
    }
  }, [location.pathname, setGridLoading]);

  return (
    <MobileNavActionsProvider slot={actionsSlot} setPresent={setActionsPresent}>
      <div
        className={cn("webapp w-full h-dvh", {
          // The `minmax(0,1fr)` content column is what constrains the market grid to the viewport width so AG Grid scrolls internally. Without an explicit mobile column template the implicit grid column defaults to `auto` (max-content), letting the grid balloon to its full content width and the whole page pan sideways on touch. Give mobile the same constrained column.
          "grid grid-cols-[minmax(0,1fr)] md:grid-cols-[max-content_minmax(0,1fr)]": !hideSidebar,
        })}
      >
        {!hideSidebar && <Sidebar />}
        <ModalDragConstraintProvider>
          <Suspense>
            <Outlet />
          </Suspense>
          <CollapsibleSettings />
        </ModalDragConstraintProvider>
      </div>
      {showMobileNav && (
        <MobileNavBar
          hideFromClassName={isCurveManagementPage ? "min-[1001px]:hidden" : "md:hidden"}
          showDefaultActions={customActions === 0}
          onActionsSlot={setActionsSlot}
        />
      )}
    </MobileNavActionsProvider>
  );
}

export function Layout() {
  return (
    <SessionProvider>
      <MfaOfferGate />
      <AppThemeProvider theme={webappTheme}>
        <UpdatePromptProvider>
          <ModalProvider>
            <ToasterContainer />
            <UpdateToastGate />
            <PermissionChangeWatcher />
            <DialogProvider>
              <LicenceAgreement>
                <LayoutContent />
              </LicenceAgreement>
            </DialogProvider>
          </ModalProvider>
        </UpdatePromptProvider>
      </AppThemeProvider>
    </SessionProvider>
  );
}
